import React, { useCallback, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, Platform, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { AnchorRect } from '@/hooks/common/useAnchoredPosition';

export interface DropdownOption {
  value: string;
  label: string;
}

interface AdminSelectDropdownProps {
  label: string;
  options: DropdownOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  variant?: 'inline' | 'anchored';
  triggerStyle?: StyleProp<ViewStyle>;
}

export const AdminSelectDropdown: React.FC<AdminSelectDropdownProps> = ({
  label,
  options,
  selectedValue,
  onSelect,
  variant = 'inline',
  triggerStyle,
}) => {
  const styles = useStyles();
  const { redesign } = useAdminColors();
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const triggerRef = useRef<View>(null);

  const selectedLabel = options.find((o) => o.value === selectedValue)?.label ?? label;

  const toggle = useCallback(() => {
    if (variant === 'anchored') {
      if (isOpen) {
        setIsOpen(false);
      } else if (triggerRef.current) {
        triggerRef.current.measureInWindow((x, y, width, height) => {
          setAnchor({ x, y, w: width, h: height });
          setIsOpen(true);
        });
      }
    } else {
      setIsOpen((prev) => !prev);
    }
  }, [variant, isOpen]);

  const close = useCallback(() => setIsOpen(false), []);

  const handleSelect = useCallback(
    (value: string) => {
      onSelect(value);
      close();
    },
    [onSelect, close],
  );

  const renderMenuItems = () => (
    <View style={styles.menuItemsContainer}>
      {options.map((option) => (
        <TouchableOpacity
          key={option.value}
          style={styles.menuItem}
          onPress={() => handleSelect(option.value)}
          activeOpacity={0.7}
        >
          <Text
            style={[
              styles.menuItemText,
              selectedValue === option.value && styles.menuItemTextActive,
            ]}
          >
            {option.label}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  if (variant === 'anchored') {
    return (
      <>
        <View ref={triggerRef} style={styles.anchoredWrap}>
          <TouchableOpacity
            style={[styles.anchoredToggle, triggerStyle]}
            onPress={toggle}
            activeOpacity={0.8}
          >
            <Text style={styles.anchoredToggleText}>{label}: {selectedLabel}</Text>
            <SVG_COMPONENTS.CHEVRON_DOWN width={12} height={12} color={redesign.text.dim} />
          </TouchableOpacity>
        </View>
        <AnchoredDropdown
          isOpen={isOpen}
          anchor={anchor}
          width={anchor?.w || 220}
          maxHeight={320}
          onRequestClose={close}
        >
          {renderMenuItems()}
        </AnchoredDropdown>
      </>
    );
  }

  return (
    <View style={styles.inlineContainer}>
      <TouchableOpacity style={styles.inlineToggle} onPress={toggle} activeOpacity={0.7}>
        <Text style={styles.inlineToggleText}>{selectedLabel}</Text>
        <View style={[styles.chevronContainer, isOpen && styles.chevronContainerRotated]}>
          <SVG_COMPONENTS.CHEVRON_DOWN width={16} height={16} color={redesign.text.dim} />
        </View>
      </TouchableOpacity>

      {isOpen && <View style={styles.inlineMenu}>{renderMenuItems()}</View>}
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components }) => {
    const admin = components.admin;
    return StyleSheet.create({
      // Inline variant (mobile)
      inlineContainer: {
        position: 'relative',
        paddingBottom: 8,
        zIndex: Platform.OS === 'web' ? 1001 : 100,
      },
      inlineToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 8,
        minHeight: 44,
      },
      inlineToggleText: {
        fontSize: 14,
        color: admin.dataTable.text,
        marginRight: 8,
      },
      chevronContainer: {
        transition: 'transform 0.2s',
      } as any,
      chevronContainerRotated: {
        transform: [{ rotate: '180deg' }],
      },
      inlineMenu: {
        position: 'absolute',
        top: 40,
        left: 0,
        right: 0,
        backgroundColor: admin.selector.dropdownBg,
        borderWidth: 1,
        borderColor: admin.selector.border,
        borderRadius: 8,
        shadowColor: '#000',
        shadowOpacity: 0.12,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 4 },
        elevation: 3,
        zIndex: 1000,
      },

      // Anchored variant (desktop)
      anchoredWrap: {
        alignSelf: 'flex-start',
      },
      anchoredToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderRadius: 8,
        backgroundColor: admin.selector.bg,
        borderWidth: 1,
        borderColor: admin.selector.border,
      },
      anchoredToggleText: {
        fontSize: 14,
        color: admin.dataTable.text,
        fontWeight: '500',
      },

      // Shared menu items (rendered both inline + inside AnchoredDropdown)
      menuItemsContainer: {
        backgroundColor: admin.selector.dropdownBg,
        borderRadius: 8,
        overflow: 'hidden',
      },
      menuItem: {
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
      },
      menuItemText: {
        fontSize: 14,
        color: admin.dataTable.text,
      },
      menuItemTextActive: {
        fontWeight: '600',
        color: admin.selector.itemSelectedText,
      },
    });
  });
